import React, { Component, ErrorInfo, ReactNode } from 'react';
import { motion } from 'framer-motion';
import AlertTriangleIcon from './icons/AlertTriangleIcon';
import RefreshCwIcon from './icons/RefreshCwIcon';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    // Log to error tracking service (e.g., Sentry)
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { extra: errorInfo });
    // }
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-950 to-zinc-900 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="max-w-2xl w-full"
          >
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-8 shadow-2xl">
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center border border-red-500/20">
                  <AlertTriangleIcon className="w-10 h-10 text-red-500" />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-3xl font-bold text-white text-center mb-3">
                Something went wrong
              </h1>

              {/* Description */}
              <p className="text-zinc-400 text-center mb-6">
                We're sorry, but an unexpected error occurred. Our team has been notified
                and is working to fix the issue.
              </p>

              {/* Error Details (Development) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="mb-6 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-auto">
                  <p className="text-red-400 font-mono text-sm mb-2">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <details className="mt-2">
                      <summary className="text-zinc-500 text-xs cursor-pointer hover:text-zinc-400">
                        Stack trace
                      </summary>
                      <pre className="text-zinc-500 text-xs mt-2 overflow-auto">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 justify-center">
                <motion.button
                  onClick={this.handleReset}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold border border-white/10 transition-all duration-200 flex items-center gap-2"
                >
                  <RefreshCwIcon className="w-4 h-4" />
                  Try Again
                </motion.button>
                <motion.button
                  onClick={this.handleReload}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-semibold shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 ring-1 ring-inset ring-white/10 transition-all duration-200"
                >
                  Reload Page
                </motion.button>
              </div>

              {/* Help Text */}
              <p className="text-zinc-500 text-sm text-center mt-6">
                If the problem persists, please contact support with error code:{' '}
                <code className="text-zinc-400 bg-zinc-900/50 px-2 py-1 rounded">
                  {this.state.error?.message || 'UNKNOWN_ERROR'}
                </code>
              </p>
              {/* Debug info for user */}
              <p className="text-xs text-zinc-600 text-center mt-2">
                {this.state.error?.name}: {this.state.error?.message}
              </p>
            </div>
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

